import React, { useState, useRef, useEffect } from 'react';
import { COLUMN_DEFINITIONS, ColumnDefinition } from './columnDefinitions';

interface ColumnSelectorProps {
  visibleColumns: string[];
  onColumnsChange: (columns: string[]) => void;
  onClose: () => void;
}

interface CategoryInfo {
  id: string;
  label: string;
  icon: string;
  description: string;
}

const CATEGORIES: CategoryInfo[] = [
  { id: 'core', label: 'Core Info', icon: '📊', description: 'Essential stock and strategy metrics' },
  { id: 'technical', label: 'Technical Analysis', icon: '📈', description: 'Chart patterns and indicators' },
  { id: 'options', label: 'Options Details', icon: '⚡', description: 'Options-specific metrics' },
  { id: 'fundamentals', label: 'Fundamentals', icon: '🏢', description: 'Company financial data' },
  { id: 'indicators', label: 'Indicators', icon: '✅', description: 'Status and availability flags' },
];

const PRESETS = {
  essential: {
    name: 'Essential',
    description: 'Core columns for options screening',
    columns: ['symbol', 'company_name', 'current_price', 'cc_score', 'best_ann_yield', 'iv_percentile', 'rsi', 'sector']
  },
  professional: {
    name: 'Professional',
    description: 'Complete analysis with all available metrics',
    columns: COLUMN_DEFINITIONS.map(col => col.id)
  }
};

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  visibleColumns,
  onColumnsChange,
  onClose
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['core']) // Core category expanded by default
  );
  const [searchTerm, setSearchTerm] = useState('');
  const selectorRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleColumn = (columnId: string) => {
    const column = COLUMN_DEFINITIONS.find(col => col.id === columnId);
    if (column?.isDefault) return; // Can't toggle default columns
    
    const newColumns = visibleColumns.includes(columnId)
      ? visibleColumns.filter(id => id !== columnId)
      : [...visibleColumns, columnId];
    
    onColumnsChange(newColumns);
  };

  const applyPreset = (presetKey: keyof typeof PRESETS) => {
    onColumnsChange(PRESETS[presetKey].columns);
  };

  const resetToDefault = () => {
    const defaultColumns = COLUMN_DEFINITIONS.filter(col => col.isDefault).map(col => col.id);
    onColumnsChange(defaultColumns);
  };

  const getColumnsByCategory = (categoryId: string) => {
    return COLUMN_DEFINITIONS.filter(col => {
      const matchesCategory = col.category === categoryId;
      const matchesSearch = searchTerm === '' || 
        col.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        col.description?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  };

  const getVisibleColumnsCount = () => visibleColumns.length;
  const getTotalColumnsCount = () => COLUMN_DEFINITIONS.length;

  return (
    <div 
      ref={selectorRef}
      className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-80 max-h-96"
      style={{ backgroundColor: '#1a1a1a', borderColor: '#333', color: '#fff' }}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-white">Customize Columns</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-lg leading-none"
          >
            ×
          </button>
        </div>
        
        {/* Search */}
        <input
          type="text"
          placeholder="Search columns..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-400 text-sm"
        />
        
        {/* Stats */}
        <div className="text-sm text-gray-400 mt-2">
          {getVisibleColumnsCount()} of {getTotalColumnsCount()} columns visible
        </div>
      </div>

      {/* Presets */}
      <div className="p-4 border-b border-gray-700">
        <h4 className="text-sm font-medium text-gray-300 mb-2">Quick Presets</h4>
        <div className="flex gap-0 rounded-lg overflow-hidden">
          {Object.entries(PRESETS).map(([key, preset]) => {
            const isActive = JSON.stringify(visibleColumns.sort()) === JSON.stringify(preset.columns.sort());
            return (
              <button
                key={key}
                onClick={() => applyPreset(key as keyof typeof PRESETS)}
                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-gray-600 text-white'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                }`}
              >
                {preset.name}
              </button>
            );
          })}
        </div>
        <button
          onClick={resetToDefault}
          className="w-full mt-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
        >
          Reset to Default
        </button>
      </div>

      {/* Column Categories */}
      <div className="max-h-64 overflow-y-auto column-selector-scroll">
        {CATEGORIES.map((category) => {
          const categoryColumns = getColumnsByCategory(category.id);
          if (categoryColumns.length === 0 && searchTerm) return null;
          
          const isExpanded = expandedCategories.has(category.id);
          const visibleInCategory = categoryColumns.filter(col => visibleColumns.includes(col.id)).length;
          
          return (
            <div key={category.id} className="border-b border-gray-700 last:border-b-0">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-800 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span>{category.icon}</span>
                  <span className="font-medium text-white">{category.label}</span>
                  <span className="text-xs text-gray-400">
                    ({visibleInCategory}/{categoryColumns.length})
                  </span>
                </div>
                <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                  ➤
                </span>
              </button>
              
              {/* Category Columns */}
              {isExpanded && (
                <div className="pb-2">
                  {categoryColumns.map((column) => (
                    <div
                      key={column.id}
                      className="px-6 py-2 flex items-center justify-between hover:bg-gray-800"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={visibleColumns.includes(column.id)}
                          onChange={() => toggleColumn(column.id)}
                          disabled={column.isDefault}
                          className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                        />
                        <div>
                          <div className="text-sm text-white flex items-center gap-2">
                            {column.label}
                            {column.isDefault && (
                              <span className="text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded">
                                Default
                              </span>
                            )}
                          </div>
                          {column.description && (
                            <div className="text-xs text-gray-400 mt-1">
                              {column.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-700 bg-gray-900">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>Changes saved automatically</span>
          <span>💾 Saved to browser</span>
        </div>
      </div>
    </div>
  );
};

export default ColumnSelector;